import { useState, useEffect, useMemo, SyntheticEvent } from "react";
import { Button, Message, Modal } from "semantic-ui-react";

// UX
import ModEditDiscount from "./ModEditDiscount";
import ModDetailInvoiceUX from "./ModDetailInvoiceUX";
import CardMedicalFeeHistoryUX from "./CardMedicalFeeHistoryUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// Utils
import { dateToStringWithoutTimeBE, formatDate, formatDatetime } from "react-lib/utils/dateUtils";
import moment from "moment";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import { useIntl } from "react-intl";
import { ENCOUNTER_OPTIONS } from "react-lib/apps/HISV3/LAB/CardLabOrderQueue";

// Styles
import "./CardMedicalFeeHistory.scss"
import { invoiceOrderStatus } from "./BILInterface";

// Const
const CARD_MEDICAL_FEE_HISTORY = "CardMedicalFeeHistory";
const MOD_EDIT_DISCOUNT = "ModEditDiscountUX";
const MOD_ADD_ITEMS = "ModAddItems";

const DEFAULT_DATE = dateToStringWithoutTimeBE(moment());

const CardMedicalFeeHistory = (props: any) => {
  const intl = useIntl();
  const [openModEdit, setOpenModEdit] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [tracking, setTracking] = useState(false);
  const [modPrintInvoice, setModPrintInvoice] = useState<boolean>(false);
  const [modDetailInvoice, setModDetailInvoice] = useState<boolean>(false);
  const [startDate, setStartDate] = useState(formatDate(DEFAULT_DATE));
  const [endDate, setEndDate] = useState(formatDate(DEFAULT_DATE));
  const [openMessage, setOpenMessage] = useState(false);
  const [orderBy, setOrderBy] = useState<string>("Item");

  const trackingStatus = props.MedicalFeeHistorySequence?.printStatus;

  useEffect(() => {
    props.runSequence({ sequence: "MedicalFeeHistory", restart: true });

    return () => {
      props.runSequence({ sequence: "MedicalFeeHistory", clear: true });
    };
  }, []);

  useEffect(() => {
    if (trackingStatus !== undefined && trackingStatus?.status !== "CANCELED") {
      setTracking(true);
    }
    if (trackingStatus?.status === "WAITING" || trackingStatus?.status === "IN_PROGRESS") {
      setTimeout(() => {
        props.runSequence({
          sequence: "MedicalFeeHistory",
          action: "trackingPrint",
          task_id: trackingStatus?.task_id,
          pdf: trackingStatus?.pdf,
        });
      }, 5000);
    }
  }, [trackingStatus]);

  const handleCloseModalTracking = () => {
    if (trackingStatus?.success || trackingStatus?.status === "CANCELED") {
      setTracking(false);
    } else {
      props.runSequence({
        sequence: "MedicalFeeHistory",
        action: "trackingPrint",
        task_id: trackingStatus?.task_id,
        pdf: trackingStatus?.pdf,
        status: "CANCEL",
      });
    }
  };

  const medicalFeeHistoryList = useMemo(() => {
    return (props.MedicalFeeHistorySequence?.MedicalFeeHistoryList || []).map((item: any) => {
      const formatPrice = "total,paid,unpaid,covered,discount".split(",").reduce(
        (r: any, k: any) => ({
          ...r,
          [k]: (
            <div style={{ textAlign: "right" }}>
              {Number(item[k]).toLocaleString("en-US", {
                style: "decimal",
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
            </div>
          ),
        }),
        {}
      );

      const filterCoverages = (data: string) => {
        let coveragesId = data?.split(", ")

        let coverageText = (coveragesId || [])?.map((id:string) => {
          return  (props.masterData?.coverage || []).filter((item: any) =>
          item?.code === id
        )?.[0]?.code_name;
        })

        return coverageText.join(", \n") || "";
      };

      return {
        ...item,
        created: formatDatetime(item.created),
        ...formatPrice,
        coverages: filterCoverages(item?.coverages),
      };
    }) as any[];
  }, [props.MedicalFeeHistorySequence?.MedicalFeeHistoryList]);

  const selectedEncounter = useMemo(() => {
    const se = props.selectedEncounter || {};

    return {
      hn: se.hn || "-",
      patient_name: se.patient_name || "-",
      coverage: se.coverage || "-",
      payer_name: se.payer_name || "-",
      id: se.id || "-",
      number: se.number || "-",
      type: se.type || "-",
      started: formatDatetime(se.started, true) || "-",
      division_name: se.division_name || "-",
      doctor_name: se.doctor_name || "-",
    };
  }, [props.selectedEncounter]);

  const formatCurrency = (value: any) =>
    Number(value || 0).toLocaleString("en-US", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
  });

  const byItemList = useMemo(() => {
    return (props.MedicalFeeHistorySequence?.invoiceItemsByItem || []).map((item: any) => ({
      ...item,
      created: formatDatetime(item.created),
      unitprice: formatCurrency(item.unitprice),
      price: formatCurrency(item.price),
      send_claim: formatCurrency(item.send_claim),
      absorb: formatCurrency(item.absorb),
      patient_claim: formatCurrency(item.patient_claim),
      non_claimable: formatCurrency(item.non_claimable),
      discount: formatCurrency(item.discount),
      drg_discount_amount: formatCurrency(item.drg_discount_amount),
    }));
  }, [props.MedicalFeeHistorySequence?.invoiceItemsByItem]);

  const byModeList = useMemo(() => {
    return (props.MedicalFeeHistorySequence?.invoiceItemsByMode || []).map((item: any) => ({
      ...item,
      price__sum: formatCurrency(item.price__sum),
      send_claim__sum: formatCurrency(item.send_claim__sum),
      absorb__sum: formatCurrency(item.absorb__sum),
      patient_claim__sum: formatCurrency(item.patient_claim__sum),
      non_claimable__sum: formatCurrency(item.non_claimable__sum),
      discount__sum: formatCurrency(item.discount__sum),
    }));
  }, [props.MedicalFeeHistorySequence?.invoiceItemsByMode]);

  const byOrderList = useMemo(() => {
    return (props.MedicalFeeHistorySequence?.invoiceItemsByOrder || []).map((item: any) => ({
      ...item,
      created: formatDatetime(item.created),
      price__sum: formatCurrency(item.price__sum),
      send_claim__sum: formatCurrency(item.send_claim__sum),
      absorb__sum: formatCurrency(item.absorb__sum),
      patient_claim__sum: formatCurrency(item.patient_claim__sum),
      non_claimable__sum: formatCurrency(item.non_claimable__sum),
      discount__sum: formatCurrency(item.discount__sum),
      pay__sum: formatCurrency(item.pay__sum),
      status__name: invoiceOrderStatus[item.status__name as keyof typeof invoiceOrderStatus]
    }));
  }, [props.MedicalFeeHistorySequence?.invoiceItemsByOrder]);

  const invoiceSummary = useMemo(() => {
    const fieldMap: Record<string, Record<string, string>> = {
      Mode: {
        price__sum: "price__sum",
        send_claim__sum: "send_claim__sum",
        absorb__sum: "absorb__sum",
        patient_claim__sum: "patient_claim__sum",
        non_claimable__sum: "non_claimable__sum",
        discount__sum: "discount__sum",
      },
      Order: {
        price__sum: "price__sum",
        send_claim__sum: "send_claim__sum",
        absorb__sum: "absorb__sum",
        patient_claim__sum: "patient_claim__sum",
        non_claimable__sum: "non_claimable__sum",
        discount__sum: "discount__sum",
      },
      Item: {
        price__sum: "price",
        send_claim__sum: "send_claim",
        absorb__sum: "absorb",
        patient_claim__sum: "patient_claim",
        non_claimable__sum: "non_claimable",
        discount__sum: "discount",
      },
    };

    const dataMap: Record<string, any[]> = {
      Item: props.MedicalFeeHistorySequence?.invoiceItemsByItem || [],
      Mode: props.MedicalFeeHistorySequence?.invoiceItemsByMode || [],
      Order: props.MedicalFeeHistorySequence?.invoiceItemsByOrder || [],
    };

    const currentList = dataMap[orderBy] || [];
    const currentFieldMap = fieldMap[orderBy] || {};

    return Object.entries(currentFieldMap).reduce((acc, [normalizedKey, actualField]) => {
      const rawTotal = currentList.reduce((sum, item) => {
        const val = parseFloat(item?.[actualField]) || 0;
        return sum + val;
      }, 0);

      acc[normalizedKey] = formatCurrency(rawTotal);
      return acc;
    }, {} as Record<string, string>);
  }, [
    orderBy,
    props.MedicalFeeHistorySequence?.invoiceItemsByItem,
    props.MedicalFeeHistorySequence?.invoiceItemsByMode,
    props.MedicalFeeHistorySequence?.invoiceItemsByOrder,
  ]);

  const handleRowProps = (state: any, rowInfo: any, column: any, instance: any) => {
    return {
      style: {
        backgroundColor:
          rowInfo?.original?.id && rowInfo?.original?.id === selectedRow?.id ? "#cccccc" : "white",
      },
      onClick: () => {
        setStartDate(moment(rowInfo.original.created, "DD/MM/YYYY HH:mm").format("DD/MM/YYYY"));
        setSelectedRow(rowInfo.original);
      },
    };
  };

  const handleCloseModEdit = () => {
    setOpenModEdit(false);

    props.setProp("errorMessage", {
      ...props.errorMessage,
      [`${MOD_EDIT_DISCOUNT}_ADD`]: null,
      [`${MOD_EDIT_DISCOUNT}_SAVE`]: null,
    });

    props.setProp("MedicalFeeHistorySequence", {
      ...props.MedicalFeeHistorySequence,
      DiscountList: [],
      discountByCoverageCode: "",
      discountByCoverageName: "",
      orderList: [],
    });
  };

  const handleChangeDate = (date: any, typeDate: any) => {
    if (typeDate === "start") {
      setStartDate(date);
    } else if (typeDate === "end") {
      setEndDate(date);
    }
  };

  const handleMedicalFeeHistoryPrint = () => {
    if (startDate === "") {
      setOpenMessage(true);
    } else {
      setOpenMessage(false);
      setModPrintInvoice(false);

      props.runSequence({
        sequence: "MedicalFeeHistory",
        action: "print",
        encounter: selectedRow?.id,
        card: CARD_MEDICAL_FEE_HISTORY,
        buttonLoadKey: `${CARD_MEDICAL_FEE_HISTORY}_PRINT`,
        startDate: startDate,
        endDate: endDate,
      });
    }
  };

  const handleMedicalFeeHistoryDetail = () => {
    props.runSequence({
      sequence: "MedicalFeeHistory",
      action: "getDetail",
      encounter: selectedRow?.id,
      card: CARD_MEDICAL_FEE_HISTORY,
      buttonLoadKey: `${CARD_MEDICAL_FEE_HISTORY}_Detail`,
    });

    setModDetailInvoice(true);
  }

  const handleCloseModal = () => {
    setStartDate(DEFAULT_DATE);
    setEndDate(DEFAULT_DATE);
    setOpenMessage(false);
    setModPrintInvoice(false);
    setModDetailInvoice(false);
    props.setProp('MedicalFeeHistorySequence.invoiceItemsFilter.isDate', true)
    props.setProp('MedicalFeeHistorySequence.invoiceItemsFilter.isBillModeType', true)
    props.setProp('MedicalFeeHistorySequence.invoiceItemsFilter.billModeType', -1)
    props.setProp('MedicalFeeHistorySequence.invoiceItemsFilter.startDate', "")
    props.setProp('MedicalFeeHistorySequence.invoiceItemsFilter.endDate', "")
  };

  const handleChangeFilter = (
    e: SyntheticEvent | null,
    data: { checked?: boolean; name: string; value: boolean | string }
  ) => {
    const value = data.checked === undefined ? data.value : data.checked;

    props.setProp(`MedicalFeeHistorySequence.medicalFeeFilter.${data.name}`, value);
  };

  const handleChangeInvoiceFilter = (
    e: SyntheticEvent | null,
    data: { checked?: boolean; name: string; value: boolean | string }
  ) => {
    const value = data.checked === undefined ? data.value : data.checked;

    props.setProp(`MedicalFeeHistorySequence.invoiceItemsFilter.${data.name}`, value);
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "MedicalFeeHistory",
      action: "Search",
      card: CARD_MEDICAL_FEE_HISTORY,
      buttonLoadKey: `${CARD_MEDICAL_FEE_HISTORY}_Search`,
    });
  };

  return (
    <div>
      <CardMedicalFeeHistoryUX
        MedicalFeeHistoryList={medicalFeeHistoryList}
        medicalFeeFilter={props.MedicalFeeHistorySequence?.medicalFeeFilter}
        selectedRow={selectedRow}
        hideButtonEdit={["หน่วยตรวจ", "หอผู้ป่วย", "ห้องผ่าตัด"].includes(props.divisionType)}
        getTrProps={handleRowProps}
        buttonPrint={
          <Button
            disabled={selectedRow ? false : true}
            color="blue"
            onClick={() => setModPrintInvoice(true)}
          >
            {"พิมพ์รายการค่าใช้จ่าย"}
          </Button>
        }
        buttonDetail={
          <Button
            disabled={selectedRow ? false : true}
            color="teal"
            onClick={handleMedicalFeeHistoryDetail}
          >
            {"แสดงรายละเอียดค่าใช้จ่าย"}
          </Button>
        }
        onEdit={() => {
          setOpenModEdit(true);
          props.runSequence({
            sequence: "MedicalFeeHistory",
            action: "openEdit",
            encounter: selectedRow?.id,
          });
        }}
        onRefresh={() => {
          props.runSequence({
            sequence: "MedicalFeeHistory",
            action: "refresh",
          });
          setSelectedRow(null);
        }}
        encounterOptions={ENCOUNTER_OPTIONS}
        onChangeFilter={handleChangeFilter}
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={`${CARD_MEDICAL_FEE_HISTORY}_Search`}
            size="small"
            title={intl.formatMessage({ id: "ค้นหา" })}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_MEDICAL_FEE_HISTORY}_Search`]}
            // callback
            onClick={handleSearch}
          />
        }
        languageUX={props.languageUX}
      />

      <Modal open={openModEdit} size="large" onClose={handleCloseModEdit}>
        <ModEditDiscount
          setProp={props.setProp}
          // seq
          runSequence={props.runSequence}
          MedicalFeeHistorySequence={props.MedicalFeeHistorySequence}
          // data
          // * ไม่ต้องส่ง encounter หากไม่ต้องการ filter by encounter
          encounterId={selectedRow?.id}
          // CommonInterface
          errorMessage={props.errorMessage}
          buttonLoadCheck={props.buttonLoadCheck}
          loadingStatus={props.loadingStatus}
          // options
          masterOptions={props.masterOptions}
        />
      </Modal>

      {/* tracking */}
      <Modal open={tracking} size="mini">
        <div>
          <div
            style={{
              backgroundColor: trackingStatus?.success
                ? "green"
                : trackingStatus?.status === "CANCELED"
                ? "red"
                : "#00B5AD",
              padding: "15px",
              fontWeight: "bold",
              color: "white",
            }}
          >
            {trackingStatus?.success
              ? "เตรียมเอกสารเสร็จสิ้น"
              : trackingStatus?.status === "CANCELED"
              ? "ยกเลิก"
              : "กำลังเตรียมเอกสาร"}
          </div>
          <div style={{ padding: "15px" }}>
            {trackingStatus?.success ? (
              <div>
                <div>{intl.formatMessage({ id: "เตรียมเอกสารเสร็จสิ้น" })}</div>
              </div>
            ) : trackingStatus?.status === "CANCELED" ? (
              <div>
                <div>{trackingStatus?.current_progress_description}</div>
              </div>
            ) : (
              <div>
                <div>{intl.formatMessage({ id: "กรุณารอสักครู่" })}</div>
                <div style={{ padding: "10px 0px" }}>
                  For IT: task_id = {trackingStatus?.task_id}
                </div>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "10px",
              }}
            >
              <Button
                basic
                color={trackingStatus?.success ? "green" : "red"}
                size="tiny"
                onClick={() => handleCloseModalTracking()}
              >
                {trackingStatus?.success || trackingStatus?.status === "CANCELED"
                  ? "ตกลง"
                  : "ยกเลิก"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={modPrintInvoice} onClose={handleCloseModal}>
        <div style={{ margin: "20px" }}>
          <div style={{ fontWeight: "bold" }}>{"ระบุข้อมูลช่วงค่าใช้จ่ายสำหรับพิมพ์ค่ารักษา"}</div>
          <hr />

          {/* error message  */}
          {props.errorMessage?.[`${CARD_MEDICAL_FEE_HISTORY}_PRINT`] && (
            <Message color="red">
              {props.errorMessage?.[`${CARD_MEDICAL_FEE_HISTORY}_PRINT`]}
            </Message>
          )}

          {openMessage && (
            <div style={{ marginBottom: "15px" }}>
              {startDate === "" && <Message color="red">กรุณาระบุค่าใน "วันที่"</Message>}

              {endDate === "" && <Message color="red">กรุณาระบุค่าใน "สิ้นสุด"</Message>}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "15px",
            }}
          >
            <div style={{ width: "33%", display: "flex", alignItems: "center" }}>
              <label style={{ fontWeight: "bold" }}>{"วันที่"}</label>
              <div style={{ color: "red" }}>*</div>

              <DateTextBox
                style={{ margin: "0px 10px" }}
                value={startDate}
                onChange={(date: any) => handleChangeDate(date, "start")}
              ></DateTextBox>
            </div>

            <div style={{ width: "33%", display: "flex", alignItems: "center" }}>
              <div
                style={{
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <label style={{ fontWeight: "bold" }}>
                  {intl.formatMessage({ id: "สิ้นสุด" })}
                </label>
                <DateTextBox
                  style={{ margin: "0px 10px" }}
                  value={endDate}
                  onChange={(date: any) => handleChangeDate(date, "end")}
                ></DateTextBox>
              </div>
            </div>

            <div style={{ width: "20%" }}>
              <ButtonLoadCheck
                // function
                setProp={props.setProp}
                onClick={handleMedicalFeeHistoryPrint}
                // data
                paramKey={`${CARD_MEDICAL_FEE_HISTORY}_PRINT`}
                buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_MEDICAL_FEE_HISTORY}_PRINT`]}
                // config
                name="PRINT"
                disabled={!selectedRow}
                color="blue"
                size="small"
                title={intl.formatMessage({ id: "พิมพ์" })}
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={modDetailInvoice}
        onClose={handleCloseModal}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        size="large"
        style={{ width: '90vw'}}
      >
        <ModDetailInvoiceUX
          buttonDetailSearch={
            <ButtonLoadCheck
              setProp={props.setProp}
              color="blue"
              paramKey={`${CARD_MEDICAL_FEE_HISTORY}_Detail`}
              size="small"
              title={intl.formatMessage({ id: "ค้นหา" })}
              buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_MEDICAL_FEE_HISTORY}_Detail`]}
              onClick={handleMedicalFeeHistoryDetail}
          />}
          selectedEncounter={selectedEncounter}
          invoiceItemsFilter={props.MedicalFeeHistorySequence?.invoiceItemsFilter}
          onChangeFilter={handleChangeInvoiceFilter}
          billModeTypes={[{key: "ALL", text: "ทุกหมวด", value: -1}, ...props.masterOptions?.billMode ?? []]}
          orderBy={orderBy}
          onChangeOrderBy={setOrderBy}
          invoiceItemsByItem={byItemList}
          invoiceItemsByMode={byModeList}
          invoiceItemsByOrder={byOrderList}
          invoiceSummary={invoiceSummary}
          onCloseButton={() => setModDetailInvoice(false)}
        />
      </Modal>
    </div>
  );
};

export default CardMedicalFeeHistory;
